import { FF_CAPTCHA } from 'constants/features';
import { ONE_MINUTE_IN_MILISECONDS } from 'constants/timers';
import { usePeertransferFlags } from 'hooks/usePeertransferFlags';
import { useMemo, useReducer } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from 'reducers/types';
import { getFeature } from 'selectors/features/features';

type CaptchaState = {
  token: string;
  isReady: boolean;
  countryCode: string;
};

type CaptchaAction =
  | {
      type: 'SET_VERIFIED';
      payload: {
        token: string;
      };
    }
  | {
      type: 'SET_READY';
      payload: boolean;
    }
  | {
      type: 'SET_COUNTRY_CODE';
      payload: string;
    };

const reducer = (state: CaptchaState, action: CaptchaAction) => {
  switch (action.type) {
    case 'SET_VERIFIED':
      return {
        ...state,
        token: action.payload.token,
      };
    case 'SET_READY':
      return { ...state, isReady: action.payload };
    case 'SET_COUNTRY_CODE':
      return { ...state, countryCode: action.payload };
    default:
      return state;
  }
};

const initialState: CaptchaState = {
  token: '',
  isReady: false,
  countryCode: '',
};

const useCaptcha = () => {
  const isEnabledFromRedux = useSelector((state: RootState) =>
    getFeature(state, FF_CAPTCHA),
  );

  const [state, dispatch] = useReducer(reducer, initialState);

  const { data: captchaFlags } = usePeertransferFlags(FF_CAPTCHA, {
    staleTime: ONE_MINUTE_IN_MILISECONDS,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    initialData: { [FF_CAPTCHA]: isEnabledFromRedux },
  });

  const actions = useMemo(
    () => ({
      setVerified: (token: string) =>
        dispatch({ type: 'SET_VERIFIED', payload: { token } }),
      setReady: (ready: boolean) =>
        dispatch({ type: 'SET_READY', payload: ready }),
      setCountryCode: (countryCode: string) =>
        dispatch({ type: 'SET_COUNTRY_CODE', payload: countryCode }),
    }),
    [],
  );

  const isEnabled = !!captchaFlags?.[FF_CAPTCHA];

  return {
    state: {
      ...state,
      isEnabled,
    },
    actions,
  };
};

export { useCaptcha };
